import * as api from "../../api/client";
import type { CompareModelId } from "../../api/client";
import { OpenRouterPicker } from "./OpenRouterPicker";
import { pixelTheme } from "../../theme/pixel";

// Shared validator-config control used by Model Compare and Prompt
// Compare. Lets the admin pick which model runs the rules-validator
// second pass and whether extended thinking is on. Both tools share
// the validator pipeline, so the control is identical — extracting
// it here keeps the two tools' SpecForms from diverging on a
// purely-visual decision.

const MODEL_LABELS: Record<CompareModelId, string> = {
  "claude-opus-4-7": "Opus 4.7",
  "claude-opus-4-6": "Opus 4.6",
  "claude-sonnet-4-6": "Sonnet 4.6",
  "claude-haiku-4-5-20251001": "Haiku 4.5",
};

export interface ValidatorConfigState {
  modelKind: "preset" | "openrouter";
  presetModel: CompareModelId;
  openrouterModel: string;
  thinking: boolean;
}

export const DEFAULT_VALIDATOR_CONFIG: ValidatorConfigState = {
  modelKind: "openrouter",
  // Admin compare tools default to the selected OpenRouter validator
  // model explicitly, rather than inheriting the Python CLI default.
  presetModel: "claude-sonnet-4-6",
  openrouterModel: "deepseek/deepseek-v4-flash",
  thinking: false,
};

/** Resolve the picker state to an ``id`` string, or ``null`` if the
 *  openrouter path is selected but the user hasn't pinned a model. */
export function resolveValidatorModel(state: ValidatorConfigState): string | null {
  if (state.modelKind === "preset") return state.presetModel;
  const trimmed = state.openrouterModel.trim();
  return trimmed === "" ? null : trimmed;
}

interface Props {
  state: ValidatorConfigState;
  onChange: (next: ValidatorConfigState) => void;
  disabled?: boolean;
}

export function ValidatorConfigRow({ state, onChange, disabled }: Props) {
  const theme = pixelTheme;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 280 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input
            type="radio"
            checked={state.modelKind === "preset"}
            onChange={() => onChange({ ...state, modelKind: "preset" })}
            disabled={disabled}
          />
          <span style={{ color: theme.ink, fontSize: 12 }}>preset</span>
        </label>
        <select
          value={state.presetModel}
          onChange={(e) =>
            onChange({
              ...state,
              presetModel: e.target.value as CompareModelId,
              modelKind: "preset",
            })
          }
          disabled={disabled}
          style={{
            padding: "6px 8px",
            background: theme.panel,
            color: theme.ink,
            border: `1px solid ${theme.panelEdge}`,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
          }}
        >
          {api.COMPARE_MODEL_IDS.map((id) => (
            <option key={id} value={id}>
              {MODEL_LABELS[id]}
            </option>
          ))}
        </select>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input
            type="radio"
            checked={state.modelKind === "openrouter"}
            onChange={() => onChange({ ...state, modelKind: "openrouter" })}
            disabled={disabled}
          />
          <span style={{ color: theme.ink, fontSize: 12 }}>openrouter</span>
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={state.thinking}
            onChange={(e) => onChange({ ...state, thinking: e.target.checked })}
            disabled={disabled}
          />
          <span style={{ color: theme.ink, fontSize: 13 }}>extended thinking</span>
        </label>
      </div>
      {state.modelKind === "openrouter" && (
        <OpenRouterPicker
          selected={state.openrouterModel ? [state.openrouterModel] : []}
          onChange={(next) =>
            onChange({ ...state, openrouterModel: next[next.length - 1] ?? "" })
          }
          single
          disabled={disabled}
        />
      )}
    </div>
  );
}
